import { type HandoverSpec } from '@/designs-dev/handover/types';
import { patterns20Spec } from '@/karehero-app/handover/patterns-2-0';
import ChatV2 from '@/karehero-app/tell-us-once/intake/ChatV2';
import {
  IntakeStyleContext,
  ONBOARDING_2_0_STYLE,
} from '@/karehero-app/tell-us-once/intake/IntakeStyle';
import CalculatorBar from '@/karehero-app/tell-us-once/benefits/CalculatorBar';
import {
  BAR_1_0,
  CALCULATOR_1_0,
  CALCULATOR_1_0_RECEIVING,
} from '@/karehero-app/tell-us-once/benefits/calculator-1-0.content';

const noop = () => {};

// A calculator question, opened in its chat, the way the calculator draws it.
const calculatorAt = (id: string) => () => (
  <IntakeStyleContext.Provider value={ONBOARDING_2_0_STYLE}>
    <ChatV2
      questions={CALCULATOR_1_0}
      startAt={CALCULATOR_1_0.findIndex((q) => q.id === id)}
      onExit={noop}
      bar={<CalculatorBar content={BAR_1_0} back />}
      inlineMic
      liveVoice
    />
  </IntakeStyleContext.Provider>
);

// More than one benefit, two ticked and Done waiting.
const ticking = () => (
  <IntakeStyleContext.Provider value={ONBOARDING_2_0_STYLE}>
    <ChatV2
      questions={CALCULATOR_1_0_RECEIVING}
      startAt={CALCULATOR_1_0_RECEIVING.findIndex(
        (q) => q.id === 'care-recipient-already-receiving-benefits',
      )}
      onExit={noop}
      bar={<CalculatorBar content={BAR_1_0} back />}
      inlineMic
      liveVoice
      optionsOpen
    />
  </IntakeStyleContext.Provider>
);

/**
 * 4.0's patterns, with the rule for when a question shows its answers. Once the
 * agent decides what to ask, it decides how they answer too, so attached
 * answers stop being the default. Written up in Notion, in the calculator's
 * interaction principles.
 */
export const patterns41Spec: HandoverSpec = {
  ...patterns20Spec,
  id: 'patterns-4-1',
  title: 'Patterns',
  patterns: [
    {
      id: 'say-back',
      name: 'Checking what we hold',
      when: "Three things or fewer, said back in the message, the way a person would: so far you've told us they live in England, are 66 or older and need help day and night. Is that right? More than three, and they go in a Does this look right? card as a list, which is easier to check than a long sentence. Trialling on the calculator.",
      group: 'ask' as const,
      render: calculatorAt('held'),
      isNew: true,
    },
    ...(patterns20Spec.patterns ?? []).flatMap((p) =>
      // 4.0 showed these on onboarding's age and location, which the rule now
      // answers in their own words. Shown on calculator questions that keep
      // their options instead.
      p.id === 'options-behind-see-options'
        ? [
            {
              ...p,
              render: calculatorAt('care-recipient-already-receiving-benefits'),
            },
          ]
        : p.id === 'answered-menu'
          ? [{ ...p, render: calculatorAt('care-recipient-has-unpaid-carer') }]
          : p.id === 'attached-answers'
            ? [
                {
                  ...p,
                  render: calculatorAt('care-recipient-savings-bracket'),
                  when: 'Only when seeing them helps someone answer: amounts in bands, like savings, or names they might not know, like which benefits they get. Up to four, joined under the message. Never for yes or no, or for something they already know.',
                  isNew: true,
                },
                {
                  id: 'many',
                  name: 'More than one',
                  when: "When they might have several, like which benefits they already get. A tap ticks a row and a second tap unticks it. Done appears once one is ticked and sends them as one reply. A row that can't go with the others, like None of these or Not sure, is quieter and answers straight away.",
                  group: 'choose' as const,
                  render: ticking,
                  isNew: true,
                },
                {
                  id: 'own-words',
                  name: 'Answered in their own words',
                  when: 'Yes or no, and things they already know: where someone lives, how old they are, when they need help. No options. They say or type it, and the agent reads it into the answer it needs, so 82 is 66 or over. Not sure always works.',
                  group: 'choose' as const,
                  render: calculatorAt('care-recipient-in-nursing-home'),
                  isNew: true,
                },
              ]
            : [p],
    ),
  ],
};
